import React, { useState, useRef, useEffect } from 'react';
import { Send, Trash2, Bot, User, Lightbulb, AlertTriangle, X, ThumbsUp, ThumbsDown, Flag } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// WebSocket Configuration
const WS_URL = process.env.REACT_APP_WS_URL || 'wss://1r5x85vspl.execute-api.us-west-2.amazonaws.com/production';

const ITOpsAdvisor = () => {
  const [showBetaModal, setShowBetaModal] = useState(true);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "👋 Hello! I'm your ITOps Advisor, an AI-powered IT operations assistant.\n\nI can help you with:\n\n• Troubleshooting IT infrastructure issues\n• Analyzing system dependencies and impacts\n• Providing resolution recommendations\n• Answering questions about IT operations best practices\n• Investigating root causes and patterns\n\nWhat can I help you with today?",
      id: 'welcome-msg'
    }
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [thinkingText, setThinkingText] = useState('');
  
  const [thinkingMessages, setThinkingMessages] = useState([]);
  const [showThinkingDetails, setShowThinkingDetails] = useState(true);
  const [streamingContent, setStreamingContent] = useState('');
  const [feedbackStates, setFeedbackStates] = useState({});
  const [reportForms, setReportForms] = useState({});
  const messagesEndRef = useRef(null);
  const sessionIdRef = useRef(null);
  const chatHistoryRef = useRef([]);
  const textareaRef = useRef(null);
  const wsRef = useRef(null);
  const isConnectingRef = useRef(false);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 150) + 'px';
    }
  }, [input]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, streamingContent, thinkingText, thinkingMessages]);

  // Cleanup WebSocket on unmount
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  // Connect to WebSocket
  const connectWebSocket = () => {
    return new Promise((resolve, reject) => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        resolve();
        return;
      }

      if (isConnectingRef.current) {
        reject(new Error('Already connecting'));
        return;
      }

      isConnectingRef.current = true;
      const ws = new WebSocket(WS_URL);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('[ITOps Advisor] ✅ WebSocket connected');
        isConnectingRef.current = false;
        resolve();
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('[ITOps Advisor] 📨 Message:', data.type);
          handleWebSocketMessage(data);
        } catch (error) {
          console.error('[ITOps Advisor] ❌ Parse error:', error);
        }
      };

      ws.onerror = (error) => {
        console.error('[ITOps Advisor] ❌ WebSocket error:', error);
        isConnectingRef.current = false;
        reject(error);
      };

      ws.onclose = () => {
        console.log('[ITOps Advisor] 🔌 WebSocket closed');
        isConnectingRef.current = false;
      };
    });
  };

  // Handle WebSocket messages
  const handleWebSocketMessage = (data) => {
    switch (data.type) {
      case 'thinking':
        const thinkingMsg = {
          id: `think-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          content: data.content,
          timestamp: new Date().toISOString(),
          type: 'thinking'
        };
        setThinkingMessages(prev => [...prev, thinkingMsg]);
        setThinkingText(prev => prev ? prev + '\n' + data.content : data.content);
        break;
      case 'agent_thinking':
        const agentThinkingMsg = {
          id: `agent-think-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          content: data.content,
          timestamp: new Date().toISOString(),
          type: 'agent_thinking'
        };
        setThinkingMessages(prev => [...prev, agentThinkingMsg]);
        setThinkingText(prev => prev ? prev + '\n' + data.content : data.content);
        break;
      case 'status':
        setThinkingText(data.content);
        break;
      case 'stream':
        setStreamingContent(prev => prev + data.content);
        break;
      case 'complete':
        // Finalize the message
        const messageId = `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const finalContent = data.completion || streamingContent;
        
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: finalContent,
          id: messageId
        }]);
        
        // Save to chat history
        chatHistoryRef.current.push({
          role: 'assistant',
          content: finalContent,
          timestamp: new Date().toISOString(),
          id: messageId
        });
        
        // Save to sessionStorage
        try {
          sessionStorage.setItem('itopsAdvisorChat', JSON.stringify(chatHistoryRef.current));
        } catch (e) {
          console.error('[ITOps Advisor] Error saving chat history:', e);
        }
        
        // Reset streaming state
        setStreamingContent('');
        setThinkingText('');
        setThinkingMessages([]);
        setIsTyping(false);
        setIsSubmitting(false);
        
        // Close WebSocket
        if (wsRef.current) {
          wsRef.current.close();
          wsRef.current = null;
        }
        break;
      case 'error':
        const errorMessage = `I encountered an error: ${data.content}. Please try again.`;
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: errorMessage,
          id: `error-${Date.now()}`
        }]);
        setStreamingContent('');
        setThinkingText('');
        setThinkingMessages([]);
        setIsTyping(false);
        setIsSubmitting(false);
        if (wsRef.current) {
          wsRef.current.close();
          wsRef.current = null;
        }
        break;
      default:
        break;
    }
  };

  const handleSend = async () => {
    if (!input.trim() || isSubmitting) return;

    const userMessage = { role: 'user', content: input.trim(), id: `user-${Date.now()}` };
    const query = input.trim();
    
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsTyping(true);
    setIsSubmitting(true);
    setThinkingText('');
    setThinkingMessages([]);
    setStreamingContent('');

    // Save to chat history
    chatHistoryRef.current.push({ role: 'user', content: query, timestamp: new Date().toISOString() });

    // Generate or reuse session ID
    if (!sessionIdRef.current) {
      sessionIdRef.current = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    }

    try {
      // Close existing WebSocket if open
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }

      await connectWebSocket();
      
      // Send the request
      wsRef.current.send(JSON.stringify({
        action: 'itops_advisor',
        inputText: query,
        sessionId: sessionIdRef.current
      }));
      
      console.log('[ITOps Advisor] 📤 Request sent');
    } catch (error) {
      console.error('[ITOps Advisor] Connection error:', error);
      
      const errorMessage = 'Failed to connect to the advisor service. Please try again.';
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: errorMessage,
        id: `error-${Date.now()}`
      }]);
      
      setIsTyping(false);
      setIsSubmitting(false);
    }
  };

  // Load chat history from sessionStorage on mount
  useEffect(() => {
    try {
      const saved = sessionStorage.getItem('itopsAdvisorChat');
      if (saved) {
        const history = JSON.parse(saved);
        chatHistoryRef.current = history;
        
        if (history.length > 0) {
          const restoredMessages = history.map(msg => ({
            role: msg.role,
            content: msg.content,
            id: msg.id
          }));
          setMessages(restoredMessages);
        }
      }
    } catch (e) {
      console.error('[ITOps Advisor] Error loading chat history:', e);
    }
  }, []);

  const handleClear = () => {
    if (!window.confirm('Are you sure you want to clear the chat history?')) return;

    // Close WebSocket
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    // Reset session
    sessionIdRef.current = null;
    chatHistoryRef.current = [];
    
    // Clear sessionStorage
    try {
      sessionStorage.removeItem('itopsAdvisorChat');
    } catch (e) {
      console.error('[ITOps Advisor] Error clearing chat history:', e);
    }

    // Reset state
    setStreamingContent('');
    setThinkingText('');
    setThinkingMessages([]);
    setIsTyping(false);
    setIsSubmitting(false);

    // Reset to welcome message
    setMessages([
      {
        role: 'assistant',
        content: "👋 Hello! I'm your ITOps Advisor, an AI-powered IT operations assistant.\n\nI can help you with:\n\n• Troubleshooting IT infrastructure issues\n• Analyzing system dependencies and impacts\n• Providing resolution recommendations\n• Answering questions about IT operations best practices\n• Investigating root causes and patterns\n\nWhat can I help you with today?",
        id: 'welcome-msg'
      }
    ]);

    console.log('[ITOps Advisor] Chat cleared, session reset');
  };

  const submitFeedback = async (messageId, feedbackType, description = '', isReport = false) => {
    console.log('[ITOps Advisor] Submitting feedback:', { messageId, feedbackType, description, isReport });
    setFeedbackStates(prev => ({ ...prev, [messageId]: { submitted: true, message: 'Thanks for your feedback!' } }));
  };

  const toggleReportForm = (messageId) => {
    setReportForms(prev => ({ ...prev, [messageId]: !prev[messageId] }));
  };

  const exampleQuestions = [
    "What's causing high latency in the API?",
    "Has this error happened before?",
    "How do we fix database connection issues?"
  ];

  return (
    <div className="h-full flex flex-col p-6 space-y-4">
      {/* Beta Modal */}
      {showBetaModal && (
        <>
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50" onClick={() => setShowBetaModal(false)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-surface rounded-card border-2 border-yellow-accent/60 max-w-2xl w-full relative"
                 >
              <button
                onClick={() => setShowBetaModal(false)}
                className="absolute top-4 right-4 text-text-secondary hover:text-text-primary transition-colors"
              >
                <X size={20} />
              </button>
              
              <div className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-full bg-yellow-accent/20 border-2 border-yellow-accent/60 flex items-center justify-center">
                    <AlertTriangle size={24} className="text-text-primary" 
                                    />
                  </div>
                  <h2 className="text-2xl font-bold text-text-primary">
                    ⚠️ Beta Release
                  </h2>
                </div>
                
                <p className="text-text-primary text-base mb-4 leading-relaxed">
                  Welcome to <span className="font-semibold text-blue-400">ITOps Advisor (Beta)</span>. This AI-powered troubleshooting assistant is actively learning and improving.
                </p>
                
                <div className="bg-dark-border/40 border border-border/60 rounded-card p-4 mb-6">
                  <h3 className="text-sm font-semibold text-blue-400 mb-3">What this means for you:</h3>
                  <ul className="space-y-2 text-sm text-text-primary">
                    <li className="flex items-start gap-2">
                      <span className="text-blue-400 mt-0.5">•</span>
                      <span>Recommendations should be treated as starting points, not final solutions</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-400 mt-0.5">•</span>
                      <span>Always verify AI advice before taking action in production environments</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-400 mt-0.5">•</span>
                      <span>All conversations are recorded and stored</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-400 mt-0.5">•</span>
                      <span>Your interactions help train and improve the AI</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-400 mt-0.5">•</span>
                      <span>Response quality will improve over time as we gather more data</span>
                    </li>
                  </ul>
                </div>
                
                <p className="text-xs text-text-secondary mb-6 leading-relaxed italic">
                  By using this feature, you acknowledge that your chat history will be used to enhance system performance and that you will exercise professional judgment when applying AI recommendations.
                </p>
                
                <button
                  onClick={() => setShowBetaModal(false)}
                  className="w-full py-3 rounded bg-blue-500/20 border border-blue-500/40 text-blue-400 hover:bg-blue-500/30 transition-all font-semibold"
                >
                  I Understand - Continue to ITOps Advisor
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-blue-400 mb-2">
            ITOps Advisor
          </h1>
          <p className="text-sm text-text-secondary">AI-powered IT operations assistant with streaming responses</p>
        </div>
        <button
          onClick={handleClear}
          className="flex items-center gap-2 px-4 py-2 rounded bg-dark-border/40 border border-border/60 text-text-secondary hover:text-blue-400 hover:border-blue-500/40 transition-all text-sm font-semibold"
        >
          <Trash2 size={16} />
          Clear Chat
        </button>
      </div>

      {/* Info Card */}
      <div className="bg-surface rounded-card border border-border p-4"
           >
        <div className="flex items-start gap-3">
          <Lightbulb size={20} className="text-text-primary mt-0.5 flex-shrink-0" 
                      />
          <div>
            <h3 className="text-sm font-semibold text-text-primary mb-2">How to use ITOps Advisor</h3>
            <p className="text-xs text-text-secondary mb-2">
              Ask questions about IT operations, incidents, system dependencies, or resolution steps. 
              The AI assistant leverages Bedrock Agent capabilities to provide intelligent responses and recommendations.
            </p>
            <p className="text-xs text-text-secondary mt-2 italic">
              Powered by AWS Bedrock Agent with WebSocket streaming
            </p>
            <div className="flex flex-wrap gap-2 mt-2">
              {exampleQuestions.map((question, idx) => (
                <button
                  key={idx}
                  onClick={() => setInput(question)}
                  className="text-xs px-3 py-1 rounded bg-dark-border/40 border border-blue-500/30 text-blue-400 hover:bg-blue-500/10 transition-all"
                >
                  "{question}"
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Chat Container */}
      <div className="flex-1 bg-surface rounded-card border border-border overflow-hidden flex flex-col"
           >
        
        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {messages.map((message, index) => (
            <div key={index} className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              {message.role === 'assistant' && (
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-500/20 border border-blue-500/40 flex items-center justify-center">
                  <Bot size={16} className="text-blue-400" />
                </div>
              )}
              
              <div className={`rounded-card p-5 ${
                message.role === 'user'
                  ? 'max-w-2xl bg-blue-500/10 border border-blue-500/30 text-text-primary'
                  : 'max-w-4xl bg-surface border border-border text-text-primary'
              }`}
                   style={message.role === 'user' ? { } : {}}>
                <div className="text-[14px] leading-7 prose prose-invert max-w-none prose-p:my-3 prose-li:my-1 prose-ul:my-3 prose-ol:my-3 prose-ul:pl-4 prose-ol:pl-4 prose-headings:my-4 prose-headings:font-semibold prose-h2:text-base prose-h3:text-[15px] prose-strong:text-blue-300 prose-code:text-orange-300 prose-code:bg-surface-2 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-[13px] prose-code:font-mono prose-pre:bg-surface-2 prose-pre:border prose-pre:border-border prose-pre:rounded-lg prose-pre:my-4 prose-a:text-blue-400 prose-a:no-underline hover:prose-a:underline prose-table:my-4 prose-th:px-3 prose-th:py-2 prose-th:text-text-secondary prose-th:bg-surface-2 prose-th:text-xs prose-td:px-3 prose-td:py-2 prose-td:border-border prose-td:text-[13px] prose-hr:border-border prose-hr:my-4 prose-blockquote:border-blue-500/40 prose-blockquote:text-text-secondary">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.content}</ReactMarkdown>
                </div>
                
                {/* Feedback Component for Assistant Messages */}
                {message.role === 'assistant' && message.id && message.id !== 'welcome-msg' && !message.id.startsWith('error-') && (
                  <div className="mt-3 pt-3 border-t border-border/30">
                    {!feedbackStates[message.id]?.submitted ? (
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-text-secondary">Was this helpful?</span>
                        <button
                          onClick={() => submitFeedback(message.id, 'positive')}
                          className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-all text-green-400 border border-green-500/30 hover:bg-green-500/20 hover:border-green-500/50"
                        >
                          <ThumbsUp size={12} />
                          Helpful
                        </button>
                        <button
                          onClick={() => submitFeedback(message.id, 'negative')}
                          className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-all text-red-400 border border-red-500/30 hover:bg-red-500/20 hover:border-red-500/50"
                        >
                          <ThumbsDown size={12} />
                          Not Helpful
                        </button>
                        <button
                          onClick={() => toggleReportForm(message.id)}
                          className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-all text-red-400 border border-red-500/30 hover:text-red-300 hover:bg-red-500/20 hover:border-red-500/50"
                        >
                          <Flag size={12} />
                          Report
                        </button>
                      </div>
                    ) : (
                      <div className="text-xs text-green-400">
                        {feedbackStates[message.id]?.message || 'Thanks for your feedback!'}
                      </div>
                    )}
                    
                    {reportForms[message.id] && (
                      <div className="mt-2 space-y-2">
                        <textarea
                          id={`issue-description-${message.id}`}
                          placeholder="Describe the issue..."
                          className="w-full px-2 py-1.5 bg-surface border border-border rounded text-xs text-text-primary resize-none"
                          rows={2}
                        />
                        <button
                          onClick={() => {
                            const description = document.getElementById(`issue-description-${message.id}`)?.value || '';
                            submitFeedback(message.id, 'negative', description, true);
                            toggleReportForm(message.id);
                          }}
                          className="px-3 py-1 bg-red-600 hover:bg-red-700 text-text-primary rounded text-xs font-medium transition-all"
                        >
                          Submit Report
                        </button>
                      </div>
                    )}
                    
                    {feedbackStates[message.id]?.error && (
                      <div className="mt-2 text-xs text-red-400">
                        {feedbackStates[message.id].error}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {message.role === 'user' && (
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center">
                  <User size={16} className="text-black" />
                </div>
              )}
            </div>
          ))}
          
          {/* Streaming Response */}
          {isTyping && (
            <div className="flex gap-3 justify-start">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-500/20 border border-blue-500/40 flex items-center justify-center">
                <Bot size={16} className="text-blue-400" />
              </div>
              <div className="max-w-4xl bg-surface border border-border rounded-card p-5 space-y-3">
                {/* Thinking indicator */}
                {(thinkingMessages.length > 0 || thinkingText) && (
                  <div className="bg-surface-2 border border-border rounded-card p-3">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
                      <span className="text-xs text-text-secondary">{thinkingText || 'Processing...'}</span>
                    </div>
                  </div>
                )}
                {/* Streaming content */}
                {streamingContent ? (
                  <div>
                    {thinkingText && <div className="border-t border-border/30 my-2" />}
                    <div className="text-[14px] text-text-primary leading-7 prose prose-invert max-w-none prose-p:my-3 prose-li:my-1 prose-ul:my-3 prose-ol:my-3 prose-ul:pl-4 prose-ol:pl-4 prose-headings:my-4 prose-headings:font-semibold prose-h2:text-base prose-h3:text-[15px] prose-strong:text-blue-300 prose-code:text-orange-300 prose-code:bg-surface-2 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-[13px] prose-code:font-mono prose-pre:bg-surface-2 prose-pre:border prose-pre:border-border prose-pre:rounded-lg prose-pre:my-4 prose-a:text-blue-400 prose-a:no-underline hover:prose-a:underline prose-table:my-4 prose-th:px-3 prose-th:py-2 prose-th:text-text-secondary prose-th:bg-surface-2 prose-th:text-xs prose-td:px-3 prose-td:py-2 prose-td:border-border prose-td:text-[13px] prose-hr:border-border prose-hr:my-4 prose-blockquote:border-blue-500/40 prose-blockquote:text-text-secondary">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{streamingContent}</ReactMarkdown>
                      <span className="inline-block w-2 h-3 bg-blue-500 ml-1 animate-pulse" />
                    </div>
                  </div>
                ) : !thinkingText && (
                  <div className="flex gap-1 items-center">
                    <span className="text-xs text-text-secondary mr-2">Connecting...</span>
                    <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" style={{ animationDelay: '0ms' }} />
                    <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" style={{ animationDelay: '150ms' }} />
                    <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" style={{ animationDelay: '300ms' }} />
                  </div>
                )}
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="border-t border-border/50 p-4">
          <div className="flex gap-2">
            <textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder="Ask about incidents, errors, or resolutions..."
              className="flex-1 px-4 py-3 bg-surface border border-border rounded text-sm text-text-primary placeholder-gray-500 focus:outline-none focus:border-blue-500/60 transition-all resize-none"
              rows={1}
            />
            <button
              onClick={handleSend}
              disabled={!input.trim() || isSubmitting}
              className="px-6 py-3 rounded bg-blue-500/20 border border-blue-500/40 text-blue-400 hover:bg-blue-500/30 disabled:opacity-30 disabled:cursor-not-allowed transition-all font-semibold text-sm flex items-center gap-2"
            >
              <Send size={16} />
              {isSubmitting ? 'Sending...' : 'Send'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ITOpsAdvisor;
